import { useRef, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, InlineLoading } from "@carbon/react";
import PageHeader from "../../../components/carbon/PageHeader/PageHeader";
import EmptyState from "../../../components/carbon/EmptyState/EmptyState";
import CoordinateOcForm, { CoordinateOcFormHandle } from "./CoordinateOcForm";
import { useCoordinateOc } from "../../../hooks/useCoordinateOc";
import { useOc } from "../../../hooks/useOc";
import { useSite } from "../../../hooks/useSites";
import "./coordinateOc.scss";

const LIST_PATH = "/purchase-orders";

/** One row of the batch master list: the OC code, a status dot, and whether it is coordinated yet. */
const CoordinateMasterRow = ({
  id,
  selected,
  done,
  onSelect,
}: {
  id: string;
  selected: boolean;
  done: boolean;
  onSelect: () => void;
}) => {
  const { t } = useTranslation();
  const { data: oc } = useOc(id);
  return (
    <Button
      kind="ghost"
      className={
        "coordinate-oc-page__master-item" +
        (selected ? " coordinate-oc-page__master-item--selected" : "")
      }
      aria-current={selected ? "true" : undefined}
      onClick={onSelect}
    >
      <span
        className={
          "coordinate-oc-page__master-dot" +
          (done ? " coordinate-oc-page__master-dot--done" : "")
        }
      />
      <span className="coordinate-oc-page__master-code">
        {oc?.code ?? id}
      </span>
      <span className="coordinate-oc-page__master-state">
        {done
          ? t("purchaseOrders.coordinate.coordinated")
          : t("purchaseOrders.coordinate.scheduled")}
      </span>
    </Button>
  );
};

/**
 * "Coordinar entrega" as a full page. Target OC id(s) come from the `ids` query param. A single id
 * is a one-column form; several ids become a master/detail: a left list of the OCs and the active
 * OC's form on the right. Confirming one marks it done and advances to the next un-coordinated OC.
 * Reached from the list's truck action and (Phase 3) straight after generating OCs.
 */
const CoordinateOcPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const ids = (params.get("ids") ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  const [activeIndex, setActiveIndex] = useState(0);
  const [done, setDone] = useState<Set<string>>(() => new Set());

  const isBatch = ids.length > 1;
  const activeId = ids[Math.min(activeIndex, Math.max(ids.length - 1, 0))];

  const { data: oc, isLoading } = useOc(activeId);
  const siteId = oc?.deliverySite?.id ?? "";
  const { data: site, isFetched: siteFetched } = useSite(siteId);
  const coordinate = useCoordinateOc();
  const formRef = useRef<CoordinateOcFormHandle>(null);

  const goToList = () => navigate(LIST_PATH);

  // No target OC: nothing to coordinate — send the buyer back to the list.
  if (ids.length === 0) {
    return <Navigate to={LIST_PATH} replace />;
  }

  const allDone = ids.every((x) => done.has(x));

  // After a successful coordinate: a single OC returns to the list; a batch marks the OC done and
  // jumps to the first still-uncoordinated OC (staying put once they are all done).
  const handleCoordinated = () => {
    if (!isBatch) {
      goToList();
      return;
    }
    const nextDone = new Set(done).add(activeId);
    setDone(nextDone);
    const nextIdx = ids.findIndex((x) => !nextDone.has(x));
    if (nextIdx !== -1) {
      setActiveIndex(nextIdx);
    }
  };

  // Mount the form only once the Site has settled, so its prefill is part of the form's initial state.
  const siteReady = !siteId || siteFetched;

  return (
    <div className="coordinate-oc-page">
      <PageHeader title={t("purchaseOrders.coordinate.title")} />
      <div
        className={
          isBatch ? "coordinate-oc-page__split" : "coordinate-oc-page__single"
        }
      >
        {isBatch ? (
          <nav className="coordinate-oc-page__master" aria-label={t("purchaseOrders.coordinate.title")}>
            {ids.map((id, index) => (
              <CoordinateMasterRow
                key={id}
                id={id}
                selected={id === activeId}
                done={done.has(id)}
                onSelect={() => setActiveIndex(index)}
              />
            ))}
          </nav>
        ) : null}

        <div className="coordinate-oc-page__detail">
          {isLoading || (oc && !siteReady) ? (
            <InlineLoading description={t("cta.loading")} />
          ) : !oc ? (
            <EmptyState
              title={t("purchaseOrders.coordinate.notFound")}
              description={t("purchaseOrders.coordinate.notFoundDescription")}
              action={
                <Button kind="ghost" onClick={goToList}>
                  {t("purchaseOrders.coordinate.backToList")}
                </Button>
              }
            />
          ) : (
            <div className="coordinate-oc-page__panel">
              <CoordinateOcForm
                key={activeId}
                ref={formRef}
                oc={oc}
                site={site}
                coordinate={coordinate}
                onSuccess={handleCoordinated}
              />
              <div className="coordinate-oc-page__actions">
                <Button kind="secondary" onClick={goToList}>
                  {allDone
                    ? t("purchaseOrders.coordinate.backToList")
                    : t("purchaseOrders.coordinate.later")}
                </Button>
                <Button
                  onClick={() => formRef.current?.submit()}
                  disabled={coordinate.isPending || done.has(activeId)}
                >
                  {t("purchaseOrders.coordinate.submit")}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoordinateOcPage;
